import { useEffect, useState } from 'react';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { isFalsy } from 'utility-types';
import type { CarouselData } from '@/interfaces/component/carousel.interface';
import FlexBox from '@/components/FlexBox';
import { DESKTOP_CONTENTS_MAX_WIDTH, MOBILE_MAX_WIDTH } from '@/app/stylesguides';
import theme from '@/app/theme';
import useCheckIsMobileSize from '@/hooks/useCheckIsMobileSize';

const Carousel = ({ dataList }: { dataList: CarouselData[] }) => {
  const isMobile = useCheckIsMobileSize();

  const [currentIndex, setCurrentIndex] = useState(0);

  const [width, setWidth] = useState(0);

  if (isFalsy(dataList)) {
    dataList = [];
  }

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1 >= dataList.length ? prevIndex : prevIndex + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? dataList.length - 1 : prevIndex - 1));
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      nextSlide();
    }, 4000);

    if (currentIndex === dataList.length - 1) {
      clearInterval(intervalId);
    }

    return () => clearInterval(intervalId);
  }, []);

  const setCarouselSize = () => {
    if (typeof window !== 'undefined') {
      const _isMobileSize = window.innerWidth <= MOBILE_MAX_WIDTH;

      const containerPadding = 20;
      const contentGap = 16;
      const screenPadding = _isMobileSize ? 16 : 20;

      const screen = window.innerWidth;

      if (_isMobileSize) {
        setWidth(screen - (screenPadding + containerPadding) * 2); //16, 20은 가로 여백
      } else {
        if (screen >= DESKTOP_CONTENTS_MAX_WIDTH) {
          const contentWidth = (DESKTOP_CONTENTS_MAX_WIDTH - contentGap) / 2;
          setWidth(contentWidth - containerPadding * 2);
        } else {
          const contentWidth = (screen - screenPadding * 2) / 2;
          setWidth(contentWidth - containerPadding * 2);
        }
      }
    }
  };
  useEffect(setCarouselSize, []);

  return (
    <FlexBox style={{ position: 'relative', width: `${width}px` }}>
      {!isMobile && currentIndex > 0 ? (
        <IconButton
          onClick={prevSlide}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '10px',
            zIndex: theme.zIndex.drawer + 1,
            padding: 0,
            color: theme.palette.blue[300]
          }}
        >
          <ArrowBackIos />
        </IconButton>
      ) : (
        ''
      )}
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <Stack
          sx={{
            width: `${width * dataList.length}px`,
            transform: `translateX(-${currentIndex * width}px)`,
            transition: 'transform 0.5s ease'
          }}
          direction={'row'}
        >
          {dataList.map((data, i) => (
            <Stack
              gap="26px"
              sx={{
                width: `${width}px`,
                alignItems: 'center'
              }}
              key={i}
            >
              <Typography variant="h6" textAlign="center" sx={{ fontWeight: 600 }}>
                {data.cardTitle}
              </Typography>
              <FlexBox style={{ width: '100%', height: '280px' }}>{data.contents}</FlexBox>
              <Stack sx={{ height: '74px', justifyContent: 'center' }}>
                {data.description.split('\n').map((line, index) => (
                  <Typography key={`${i}-${index}`} variant="subtitle2" textAlign="center">
                    {line}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
        {isMobile ? (
          <FlexBox
            style={{
              width: `${width}px`,
              paddingY: '24px',
              gap: '8px'
            }}
          >
            {dataList.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                style={{
                  display: 'inline-flex',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.blue[currentIndex === i ? 500 : 300]
                }}
              />
            ))}
          </FlexBox>
        ) : (
          ''
        )}
      </Box>

      {!isMobile && currentIndex < dataList.length - 1 ? (
        <IconButton
          onClick={nextSlide}
          sx={{
            position: 'absolute',
            top: '50%',
            right: '10px',
            zIndex: theme.zIndex.drawer + 1,
            padding: 0,
            color: theme.palette.blue[300]
          }}
        >
          <ArrowForwardIos />
        </IconButton>
      ) : (
        ''
      )}
    </FlexBox>
  );
};

export default Carousel;
